/**
 * Catch a file paste anywhere inside a scope element and hand the files to the
 * caller.
 *
 * This is the mechanism under every paste-to-attach surface. It deliberately does
 * NOT know what happens to the files: the entity detail zone uploads them against
 * an entity, `FileUpload` uploads them the way its picker does, and
 * `PendingFileUpload` and the create dialogs park them in pending state until the
 * entity being created exists. All of them get the same answer to "was this paste
 * meant to attach something, and did this scope own it".
 */
import { useEffect, useRef } from 'react';
import { filesFromClipboard } from '../clipboardFiles';

/**
 * Scope ELEMENTS currently listening, in mount order.
 *
 * A paste with nothing focused targets `document.body`, which is inside no
 * scope's subtree — so every mounted scope would otherwise claim it and one
 * screenshot would land in two places at once (a create dialog open over an
 * entity detail page is exactly this shape). Only the topmost scope handles the
 * unfocused case. A paste that lands *inside* a scope is unambiguous and always
 * belongs to that scope.
 *
 * Keyed by the scope ELEMENT, not by one entry per hook: a single form can point
 * two controls at the same scope and split the files between them with
 * `pasteFilter` (a product's photos and its documents). Those are one scope, so
 * both must get their chance — keying per hook let only the last-mounted one
 * run, and a screenshot its filter rejected was silently dropped instead of
 * reaching the control that wanted it.
 */
const mountedScopes: Array<React.RefObject<HTMLElement | null>> = [];

/**
 * Paste events THIS mechanism has already claimed.
 *
 * Claiming calls `preventDefault`, and the guard below reads `defaultPrevented`
 * to stand down for a descendant editor that owns its own paste. Those two facts
 * collide for co-scoped controls: the first one to run would look, to its own
 * sibling, exactly like a comment editor that had taken the event. Recording our
 * own claims tells the two apart — a sibling continues, a foreign handler still
 * stops us.
 */
const claimedEvents = new WeakSet<ClipboardEvent>();

interface UsePasteFilesIntoOptions {
  /** The element a paste must land inside (a dialog body, a page wrapper). */
  scopeRef: React.RefObject<HTMLElement | null> | undefined;
  /** Called with the pasted files. Never called with an empty array. */
  onFiles: (files: File[]) => void;
  /** Skip listening entirely (view-only, submitting, no scope yet). */
  disabled?: boolean;
}

export function usePasteFilesInto({ scopeRef, onFiles, disabled = false }: UsePasteFilesIntoOptions): void {
  // Callers pass a fresh closure every render. Holding it in a ref keeps the
  // effect's dependencies stable, so the listener is registered once per mount
  // instead of churning the scope stack on every keystroke in the dialog.
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  const active = !disabled && !!scopeRef;

  useEffect(() => {
    if (!active || !scopeRef) return;

    mountedScopes.push(scopeRef);

    const onPaste = (e: ClipboardEvent) => {
      // A descendant that owns image paste for its own target — a comment
      // editor, a chat composer — has already called preventDefault by the time
      // this bubbles to the document. Claiming it too would file the same
      // screenshot twice. A claim by one of OUR co-scoped siblings is not that.
      if (e.defaultPrevented && !claimedEvents.has(e)) return;

      const target = e.target as Node | null;
      const inScope = !!target && !!scopeRef.current?.contains(target);
      const topmostScope = mountedScopes[mountedScopes.length - 1]?.current ?? null;
      const unfocused =
        (target === document.body || target === document.documentElement) &&
        !!scopeRef.current &&
        scopeRef.current === topmostScope;
      if (!inScope && !unfocused) return;

      const files = filesFromClipboard(e.clipboardData);
      if (files.length === 0) return;
      claimedEvents.add(e);
      e.preventDefault();
      onFilesRef.current(files);
    };

    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('paste', onPaste);
      // Remove one entry for this ref — co-scoped hooks each pushed their own.
      const index = mountedScopes.indexOf(scopeRef);
      if (index !== -1) mountedScopes.splice(index, 1);
    };
  }, [active, scopeRef]);
}
